// src/modules/documentacion/pages/GuiaPlataformasPage.jsx
// Guia interna: como funciona el modulo de Plataformas de rastreo
// (Wialon / Leasecontrol / TRS Rastreo) en Panel Global e Historial de Cliente.
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";

const Seccion = ({ titulo, defaultExpanded, children }) => (
  <Accordion defaultExpanded={defaultExpanded} disableGutters variant="outlined" sx={{ "&:before": { display: "none" } }}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Typography sx={{ fontWeight: 700 }}>{titulo}</Typography>
    </AccordionSummary>
    <AccordionDetails>
      <Stack spacing={1.25}>{children}</Stack>
    </AccordionDetails>
  </Accordion>
);

const P = ({ children }) => (
  <Typography variant="body2" color="text.secondary">
    {children}
  </Typography>
);

const Muestra = ({ color = "#5c6bc0", label }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <Box sx={{ width: 16, height: 16, borderRadius: 0.5, bgcolor: color, flexShrink: 0 }} />
    <Typography variant="body2">{label}</Typography>
  </Stack>
);

export default function GuiaPlataformasPage() {
  const navigate = useNavigate();

  return (
    <DashboardLayout>
      <PageHeader
        title="Plataformas de rastreo"
        subtitle="Documentación"
        showBack
        onBack={() => navigate("/documentacion")}
      />

      <Box sx={{ maxWidth: 820, mx: "auto", p: { xs: 1, md: 1.5 } }}>
        <Stack spacing={1.5}>
          <Alert severity="info">
            Esta guía cubre el módulo de "Plataforma de rastreo" tal como aparece en{" "}
            <strong>Historial de Cliente</strong> (pestañas Wialon / Leasecontrol / TRS Rastreo) y en el árbol
            equivalente de <strong>Panel Global</strong>. Es documentación interna — pensada para el equipo, no
            para el cliente final.
          </Alert>

          <Seccion titulo="1. Qué es esta pantalla" defaultExpanded>
            <P>
              Muestra, para un cliente puntual de SORT, los <strong>usuarios</strong> que tiene en su plataforma de
              rastreo (Wialon, Leasecontrol o TRS Rastreo), los <strong>grupos</strong> (cuadrillas/grupos de
              unidades) a los que pertenece cada usuario, y los <strong>vehículos/unidades</strong> dentro de cada
              grupo. Un mismo cliente puede tener presencia en más de una plataforma a la vez — por eso en
              Historial de Cliente hay tres pestañas separadas ("Wialon", "Leasecontrol", "TRS Rastreo") en vez de
              una sola que intente adivinar cuál mostrar.
            </P>
          </Seccion>

          <Seccion titulo="2. Cómo se vincula un cliente de SORT con su plataforma">
            <P>
              El mecanismo de vínculo es <strong>distinto en cada plataforma</strong> — no hay una única fuente de
              verdad común:
            </P>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Plataforma</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Cómo se sabe qué usuario pertenece a qué cliente</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Wialon / Leasecontrol</TableCell>
                    <TableCell>
                      Tabla puente propia de SORT (usuario externo ↔ cliente). Se llena de dos formas: manualmente
                      con <em>"Vincular usuario"</em> (buscas el usuario por nombre en Wialon/Leasecontrol y lo
                      asocias), o de forma masiva con el botón <em>"Sincronizar Wialon/Leasecontrol"</em> de{" "}
                      <code>/panel-global</code>, que cruza la serie de los modems que SORT tiene registrados
                      contra las unidades que cada usuario ve en Wialon/Leasecontrol.
                      <br />
                      En las pestañas de <strong>Historial de Cliente</strong>, en cambio, ese cruce por serie de
                      modem se calcula <strong>en vivo</strong> cada vez que abres la pestaña — no depende de que
                      la sincronización masiva se haya corrido antes.
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>TRS Rastreo</TableCell>
                    <TableCell>
                      No hay tabla puente: el propio usuario de TRS Rastreo tiene un campo{" "}
                      <code>usu_cliente</code> que vale directamente el código de servicio de SORT. Se completa
                      solo al crear el usuario con <em>"Registrar usuario"</em> desde el árbol de un cliente (toma
                      el código del cliente donde estás parado). Hoy <strong>no se puede reasignar</strong> un
                      usuario TRS ya creado a otro cliente desde esta pantalla.
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Seccion>

          <Seccion titulo="3. Cómo leer el árbol">
            <P>
              Estructura: <strong>usuario</strong> → <strong>grupos</strong> a los que pertenece → vehículos/
              unidades de cada grupo. Los colores de la cabecera de cada grupo tienen significado:
            </P>
            <Muestra color="#5c6bc0" label="Grupo normal." />
            <Muestra color="#2e7d32" label="El grupo tiene al menos una unidad con placa ya registrada en SORT." />
            <Muestra
              color="#64748b"
              label='"Vehículos/Unidades sin grupo" — no es un grupo real, es donde caen las unidades vinculadas directo al usuario, sin pasar por ningún grupo.'
            />
            <P>
              Los grupos se muestran siempre, aunque tengan 0 unidades — así se nota de inmediato si algo quedó
              mal vinculado, en vez de esconderse en una leyenda aparte.
            </P>
          </Seccion>

          <Seccion titulo="4. Acciones sobre un usuario (menú ⋮ de su tarjeta)">
            <Stack spacing={1}>
              <Typography variant="body2">
                <strong>Editar usuario</strong> — nombre, contraseña, estado activo/inactivo, y (según la
                plataforma) el cliente/servicio vinculado.
              </Typography>
              <Typography variant="body2">
                <strong>Asignar vehículo</strong> — vincula una unidad <em>ya existente</em> directo al usuario,
                sin pasar por ningún grupo (termina en la rama "sin grupo"). Distinto de "Registrar vehículo", que
                crea una unidad nueva.
              </Typography>
              <Typography variant="body2">
                <strong>Asignar grupo</strong> — suma al usuario a un grupo <em>ya existente</em>, elegido de una
                lista con buscador. Distinto de "Registrar grupo", que crea un grupo nuevo.
              </Typography>
              <Typography variant="body2">
                <strong>Desvincular</strong> (solo Wialon/Leasecontrol) — quita el vínculo entre ese usuario y el
                cliente actual. No borra el usuario de la plataforma, solo la asociación con este cliente de SORT.
              </Typography>
            </Stack>
          </Seccion>

          <Seccion titulo="5. Acciones sobre un grupo (menú ⋮ de su cabecera)">
            <Typography variant="body2">
              <strong>Editar grupo</strong> — nombre, y qué usuarios/unidades tiene.
            </Typography>
            <Typography variant="body2">
              <strong>Desvincular grupo</strong> — quita únicamente al usuario que estás viendo de ese grupo
              puntual. El grupo sigue existiendo con sus demás usuarios y unidades intactos.
            </Typography>
            <Alert severity="warning" variant="outlined" sx={{ mt: 0.5 }}>
              Wialon/Leasecontrol editan un grupo mandando la <strong>lista completa final</strong> de miembros
              (reemplazo total); TRS Rastreo edita por <strong>delta</strong> (solo a quién agregar/quitar). La UI
              ya se encarga de armar el pedido correcto para cada una — no hace falta pensarlo al usar los botones,
              pero es la explicación de por qué el código de estos dos casos es tan distinto por dentro.
            </Alert>
          </Seccion>

          <Seccion titulo="6. Botones de arriba: Registrar grupo / vehículo / usuario">
            <P>
              Crean un registro <strong>nuevo</strong> en la plataforma correspondiente — no vinculan algo que ya
              existe (para eso están "Asignar grupo"/"Asignar vehículo" del punto 4). "Registrar grupo" y
              "Registrar vehículo" permiten, de una, sumarles usuarios/grupos existentes al crearlos.
            </P>
          </Seccion>

          <Seccion titulo="7. Buscador de vehículos/unidades">
            <P>
              Dentro de un usuario expandido, el buscador filtra por nombre, serie o placa <strong>en todos sus
              grupos a la vez</strong> — no hace falta ir abriendo caja por caja para encontrar una unidad
              puntual.
            </P>
          </Seccion>

          <Divider />

          <Seccion titulo="Notas técnicas (para quien mantiene este módulo)">
            <Typography variant="body2">
              <strong>TRS Rastreo tiene dos backends posibles:</strong> uno viejo en Django (
              <code>trs_rastreo_web</code>, el que corre en producción en trsrastreo.com.bo) y uno nuevo en
              FastAPI (<code>trs_web_bo_backend</code>, pensado para reemplazarlo manteniendo el mismo contrato{" "}
              <code>/api-sort/v1/</code>). Cuál de los dos responde depende de <code>RASTREO_BO_URL</code> en el{" "}
              <code>.env</code> del backend de SORT. Si algo funciona distinto entre el entorno local y
              producción, lo primero a revisar es cuál de los dos está atendiendo esa URL.
            </Typography>
            <Typography variant="body2">
              <strong>El matching Wialon/Leasecontrol por serie de modem</strong> (usado tanto por "Sincronizar"
              como por las pestañas de Historial de Cliente) compara los modems que <em>SORT</em> tiene
              registrados contra las unidades que ve cada usuario externo — nunca al revés. Una unidad creada
              directo en Wialon/Leasecontrol que nunca se registró en SORT no puede aparecer acá por más que se
              sincronice.
            </Typography>
            <Typography variant="body2">
              <strong>Cachés a tener en cuenta:</strong> el resumen de usuarios/unidades de Wialon y Leasecontrol
              se cachea en el backend por 1 hora — un cambio hecho recién en Wialon puede tardar hasta ese tiempo
              en reflejarse acá.
            </Typography>
          </Seccion>
        </Stack>
      </Box>
    </DashboardLayout>
  );
}
